// The one busy state for a server-rendered button (DESIGN.md BUTTON-8, owner
// 2026-10-21): while its request is in flight a button keeps its label, shows
// the `.gl-btn-spinner` in its icon's place, carries `aria-busy="true"` and
// takes no second click. Busy is not disabled: `_buttons.scss` keeps a busy
// button's rest paint even while it is `:disabled`, so it never turns grey
// mid-request.
//
// Two layers:
//   * markButtonBusy / clearButtonBusy — the markup and aria-busy only. The
//     global Turbo submit hook (lib/turbo_submit_busy.js) uses these, because
//     Turbo already disables and re-enables the submitter itself.
//   * setButtonLoading — the same plus `disabled`, for a Stimulus controller
//     driving its own request. Mirrors lead_order_form_controller's in-
//     controller setButtonLoadingState — use this shared version in new code.
//
// There is no label option: BUTTON-8 bans swapping the label to "Saving…".
// A third argument from an older caller is ignored.

const SPINNER_HTML = '<span class="gl-btn-spinner" role="status" aria-hidden="true"></span>'

// A leading icon (`<i class="ph …">`) gives its place to the spinner, so the
// button does not grow and the label does not shift.
function busyMarkup(originalHTML) {
  const template = document.createElement("template")
  template.innerHTML = originalHTML
  const content = template.content
  const first = Array.from(content.childNodes).find(
    (node) => !(node.nodeType === Node.TEXT_NODE && node.textContent.trim() === "")
  )
  if (first && first.nodeType === Node.ELEMENT_NODE && first.tagName === "I") first.remove()
  const wrapper = document.createElement("div")
  wrapper.appendChild(content)
  return `${SPINNER_HTML}${wrapper.innerHTML.trimStart()}`
}

export function isButtonBusy(button) {
  return Boolean(button) && button.getAttribute("aria-busy") === "true"
}

// Spinner + aria-busy; the label stays. An <input type=submit> cannot hold a
// spinner, so it only takes aria-busy. Calling it twice never re-captures the
// spinner markup as the "original".
export function markButtonBusy(button) {
  if (!button) return
  button.setAttribute("aria-busy", "true")
  if (button instanceof HTMLInputElement) return
  if (button.dataset.originalContent == null) {
    button.dataset.originalContent = button.innerHTML
  }
  button.innerHTML = busyMarkup(button.dataset.originalContent)
}

export function clearButtonBusy(button) {
  if (!button) return
  button.removeAttribute("aria-busy")
  if (button.dataset.originalContent != null) {
    button.innerHTML = button.dataset.originalContent
    delete button.dataset.originalContent
  }
}

export function setButtonLoading(button, loading) {
  if (!button) return

  if (loading) {
    button.disabled = true
    markButtonBusy(button)
    return
  }

  button.disabled = false
  clearButtonBusy(button)
}
